import { SagaIterator } from 'redux-saga';
import { call, select } from 'redux-saga/effects';
import {
    KintekiArrowModel,
    EntekiArrowModel,
    MakiwaraArrowModel,
    AllModel,
} from '@app/react/3d/ArrowModelInterface';
import * as TexHandler from '@app/react/3d/texHandler';
import { PartsProduct, MAKIWARA_THREAD_MODE } from '@app/react/const/type';
import type { AppState } from '@app/react/reducer';
import {
    CARVING_TEXT_COLOR_TEX_PATH,
    MAKIWARA_THREAD_MODE_C,
    MAKIWARA_THREAD_MODE_B,
} from '@app/react/const/variables';
import {
    GetPartsHandler,
    fetchAndApply,
    fetchAndApply2,
    fetchAndApplyMultiple,
    fetchAndApplyFamilycrestColor,
    FamilycrestColorTextureData,
    fetchAndApplyWithMakiwaraFeather,
} from './common';

export function* shaftFetchAndApplyWithModel(model: AllModel): SagaIterator<void> {
    const apply = TexHandler.shaft(model);
    const getParts: GetPartsHandler = (state) => state.customize.shaft;
    yield call(
        fetchAndApply(apply, getParts, 'shaft', () => model.resetShaft()),
    );
}

export function* haneFetchAndApplyWithModel(model: KintekiArrowModel): SagaIterator<void> {
    const apply = TexHandler.kintekiHane(model);
    const getParts: GetPartsHandler = (state) => state.customize.feather;
    yield call(
        fetchAndApply2(apply, getParts, 'feather', () => model.resetFeather()),
    );
}

export function* haneFetchAndApplyWithEntekiModel(model: EntekiArrowModel): SagaIterator<void> {
    const apply = TexHandler.entekiHane(model);
    const getParts: GetPartsHandler = (state) => state.customize.feather;
    yield call(
        fetchAndApply2(apply, getParts, 'feather', () => model.resetFeather()),
    );
}

export function* haneFetchAndApplyWithMakiwaraModel(model: MakiwaraArrowModel): SagaIterator<void> {
    const apply = TexHandler.makiwaraHane(model);
    const getParts: GetPartsHandler = (state) => state.customize.feather;
    const getMode = (state: AppState): MAKIWARA_THREAD_MODE => state.customize.threadMode;

    yield call(
        fetchAndApplyWithMakiwaraFeather(
            apply,
            TexHandler.nonFeather(model),
            getParts,
            getMode,
            'feather',
            () => model.resetFeather(),
        ),
    );
}

export function* threadFetchAndApplyWithModel(model: AllModel): SagaIterator<void> {
    const gloss: boolean = yield select(
        (state: AppState) => state.customize.threadGloss,
    );

    const apply = TexHandler.thread(model, gloss);
    const getParts: GetPartsHandler = (state) => state.customize.thread;
    yield call(
        fetchAndApply(apply, getParts, 'thread', () => model.resetThread()),
    );
}

export function* threadGlossWithModel(model: AllModel): SagaIterator<void> {
    const gloss: boolean = yield select(
        (state: AppState) => state.customize.threadGloss,
    );
    const type = gloss ? '' : 'matte';
    model.setThreadShiny(type);
}

export function* kebikiFetchAndApplyWithModel(model: AllModel): SagaIterator<void> {
    const apply = TexHandler.kebiki(model);
    const getParts: GetPartsHandler = (state) => state.customize.kebiki;
    yield call(
        fetchAndApply(apply, getParts, 'kebiki', () => model.resetKebiki()),
    );
}

export function* nockFetchAndApplyWithModel(model: AllModel): SagaIterator<void> {
    const apply = TexHandler.nock(model);
    const getParts: GetPartsHandler = (state) => state.customize.nock;
    yield call(
        fetchAndApply2(apply, getParts, 'nock', () => model.resetNock()),
    );
}

export function* arrowheadFetchAndApplyWithModel(model: AllModel): SagaIterator<void> {
    const apply = TexHandler.arrowHead(model);
    const getParts: GetPartsHandler = (state) => state.customize.arrowhead;
    yield call(
        fetchAndApply(apply, getParts, 'arrowhead', () => model.resetArrowhead()),
    );
}

export function* twineFetchAndApplyWithModel(model: AllModel): SagaIterator<void> {
    const apply = TexHandler.twine(model);
    const getParts: GetPartsHandler = (state) => state.customize.twine;
    yield call(
        fetchAndApply2(apply, getParts, 'twine', () => model.resetTwine()),
    );
}
export function* lameFetchAndApplyWithModel(model: AllModel): SagaIterator<void> {
    const apply = TexHandler.lame(model);
    const getParts: GetPartsHandler = (state) => state.customize.lame;
    yield call(
        fetchAndApply2(apply, getParts, 'lame', () => model.resetLame()),
    );
}

export function* familycrestFetchAndApplyWithModel(model: KintekiArrowModel): SagaIterator<void> {
    const apply = TexHandler.familyCrest(model);
    const getParts: GetPartsHandler = (state) => state.customize.familycrest;
    yield call(
        fetchAndApply(apply, getParts, 'familycrest', () => model.resetFamilycrest()),
    );
}

export function* familycrestColorFetchAndApplyWithModel(
    model: KintekiArrowModel,
): SagaIterator<void> {
    const apply = TexHandler.familyCrestColor(model);
    type H = (state: AppState) => FamilycrestColorTextureData | null;
    const getParts: H = (state) => state.customize.familycrestColor;
    yield call(
        fetchAndApplyFamilycrestColor(apply, getParts, 'familycrestColor', () => model.resetFamilycrest()),
    );
}

export function* carvingColorFetchAndApplyWithModel(model: AllModel): SagaIterator<void> {
    const apply = TexHandler.carvingColor(model);
    const getParts = () => ({ name: 'silver', textureImages: [CARVING_TEXT_COLOR_TEX_PATH], options: {} });
    yield call(
        fetchAndApply(apply, getParts, 'carvingColor', () => model.writeHorizontal('')),
    );
}

export function* carvingFetchAndApplyWithModel(model: AllModel): SagaIterator<void> {
    const selector = (state: AppState) => state.customize.carving;
    const carving: PartsProduct | null = yield select(selector);
    const selectCStr = (state: AppState) => state.customize.carvingString ?? '';
    const carvingStr: string = yield select(selectCStr);
    if (carving === null) {
        model.writeVertical('');
        console.error('carving product not found');
        return;
    }

    const writeType = carving.options?.carvingWritingType;
    const fontFamily = carving.options?.carvingFontName;
    const writer = ((direction): typeof model.writeVertical => {
        if (direction === 1) {
            return model.writeVertical.bind(model);
        } if (direction === 2) {
            return model.writeHorizontal.bind(model);
        }
        console.error(
            '[saga] carving: noting direction. recover vertical write',
        );
        return model.writeVertical.bind(model);
    })(writeType);

    if (fontFamily === undefined) {
        console.error(
            '[saga] carving: noting fontfamily. recerver default font',
        );
        writer(carvingStr);
        return;
    }

    writer(carvingStr, fontFamily);
}

export function* decorationFetchAndApplyWithModel(model: AllModel): SagaIterator<void> {
    const apply = TexHandler.decoration(model);
    const getParts = (state: AppState) => state.customize.decoration;
    yield call(
        fetchAndApplyMultiple(apply, getParts, 'decoration', () => model.resetDecoration()),
    );
}

export function* featherinsideFetchAndApplyWithModel(model: AllModel): SagaIterator<void> {
    const apply = TexHandler.featherinside(model);
    const getParts = (state: AppState) => state.customize.featherinside;
    yield call(
        fetchAndApply2(apply, getParts, 'featherinside', () => model.resetFeatherinside()),
    );
}

export function* changeThreadModeWithModel(model: MakiwaraArrowModel): SagaIterator<void> {
    const mode: MAKIWARA_THREAD_MODE = yield select(
        (state: AppState) => state.customize.threadMode,
    );
    if (mode === MAKIWARA_THREAD_MODE_C) {
        model.modeC();
    } else if (mode === MAKIWARA_THREAD_MODE_B) {
        model.modeB();
    }
}
